import * as THREE from 'three';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, breadSlice, stickLegs, checkNpcClick } from '../entities/townsfolk.js';
import { HUD } from '../ui/hud.js';
import { getCrumbs, spendCrumbs } from '../core/save.js';
import { questsForGiver, acceptQuest, turnIn, isReady as isQuestReady, objectiveProgress, QUESTS } from '../core/quests.js';

// Sheriff Station — a small office off the town square. Deputy Donut minds the
// desk while Sheriff Berry patrols outside. Bring supplies to keep the jail in
// shape.

export function createSheriffScene(){
  return () => new SheriffScene();
}

class SheriffScene {
  constructor(){
    this.panels = [];

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xD8C8B0);

    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 11;
    this.camTarget = new THREE.Vector3(0, 1.5, 0);
    this.azimuth = 0; this.polar = 0.45;

    this._buildRoom();
    this._buildFurniture();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 3.2);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.deputy = this._makeDeputy();
    this.scene.add(this.deputy.root);

    this.hotspots = [
      { pos: this.deputy.root.position.clone(), r: 2.3, label: '🗣 talk to Deputy Donut', action: () => this._talkToDeputy() },
      { pos: new THREE.Vector3(0, 0, 5.4), r: 2.2, label: '← back to downtown', action: () => this.manager.goTo('downtown', { from: 'sheriff' }) }
    ];
    this.nearSpot = null;
  }

  _buildRoom(){
    this.scene.add(new THREE.AmbientLight(0xFFFFFF, 0.8));
    const key = new THREE.DirectionalLight(0xFFF6E8, 0.5);
    key.position.set(3, 10, 5); key.castShadow = true; key.shadow.mapSize.set(1024, 1024);
    this.scene.add(key);

    const floor = new THREE.Mesh(new THREE.PlaneGeometry(14, 14), new THREE.MeshLambertMaterial({ color: 0x9A8A72 }));
    floor.rotation.x = -Math.PI/2; floor.receiveShadow = true; this.scene.add(floor);

    const wallMat = new THREE.MeshLambertMaterial({ color: 0xC8B89A });
    const back = new THREE.Mesh(new THREE.PlaneGeometry(14, 8), wallMat);
    back.position.set(0, 4, -6); back.receiveShadow = true; this.scene.add(back);
    const sideL = back.clone(); sideL.rotation.y = Math.PI/2; sideL.position.set(-7, 4, 0); this.scene.add(sideL);
    const sideR = back.clone(); sideR.rotation.y = -Math.PI/2; sideR.position.set(7, 4, 0); this.scene.add(sideR);

    this.exitDoor = box(1.5, 2.4, 0.3, C.crustDark, 0, 1.2, 5.9, this.scene);
    const sign = textSprite('← to Town', 26, '#6B4A2F', 2.6, 0.8);
    sign.position.set(0, 2.9, 5.7); this.scene.add(sign);
  }

  _buildFurniture(){
    // desk
    const desk = new THREE.Group(); desk.position.set(-2.5, 0, -2); this.scene.add(desk);
    box(2.6, .12, 1.4, 0x6E4A35, 0, .8, 0, desk);
    box(.12, .8, .12, 0x6E4A35, -1.1, .4, -.6, desk, 4);
    box(.12, .8, .12, 0x6E4A35,  1.1, .4, -.6, desk, 4);
    box(.2, .05, .3, 0x333333, -.8, .88, .3, desk); // inkwell
    cyl(.03, .03, .18, 0x333333, -.8, 1.0, .3, desk, 6); // pen

    // jail cell bars in the back corner
    const cell = new THREE.Group(); cell.position.set(3.2, 0, -3.8); this.scene.add(cell);
    box(2.4, 2.6, .1, 0x4A4A4A, 0, 1.3, -1.2, cell);
    box(2.4, 2.6, .1, 0x4A4A4A, 0, 1.3,  1.2, cell);
    box(.1, 2.6, 2.5, 0x4A4A4A, -1.2, 1.3, 0, cell);
    for (let i = -1; i <= 1; i++) cyl(.04, .04, 2.4, 0x777777, i * .55, 1.3, 0, cell, 8).rotation.x = Math.PI/2;

    // wanted board
    const board = box(2.2, 1.6, .08, 0x5A4A2A, -4.8, 2.2, -2, this.scene);
    board.rotation.y = Math.PI/2;
    for (let i = 0; i < 3; i++){
      const paper = box(.45, .55, .02, 0xFFF6E8, -4.74, 2.3 + (i-1)*.45, -2.3 + i*.7, this.scene);
      paper.rotation.y = Math.PI/2;
    }
  }

  _makeDeputy(){
    const build = (g) => {
      breadSlice(g, C.crust, C.toast, 0.6, 1.1);
      sphere(.07, 0x2E2018, -.16, 1.18, .3, g, 8);
      sphere(.07, 0x2E2018,  .16, 1.18, .3, g, 8);
      // deputy badge
      const badge = new THREE.Mesh(new THREE.CylinderGeometry(.08, .08, .02, 6), new THREE.MeshLambertMaterial({ color: 0xFFD700 }));
      badge.rotation.x = Math.PI/2; badge.position.set(0, .55, .24); g.add(badge);
      // blue uniform
      box(.62, .7, .38, 0x3D5FC0, 0, .45, 0, g);
      box(.16, .7, .4, 0x3D5FC0, -.24, .45, 0, g);
      box(.16, .7, .4, 0x3D5FC0,  .24, .45, 0, g);
      stickLegs(g, .5);
    };
    return new Townsperson({
      name: 'Deputy Donut', line: 'sheriff is out on patrol.',
      pos: new THREE.Vector3(-2.5, 0, -.4), facing: Math.PI * .85,
      wander: 0, speed: 0, bubbleY: 2.6, build
    });
  }

  _talkToDeputy(){
    this.bread.stop();
    const to = new THREE.Vector3().subVectors(this.deputy.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z);
    this.bread.root.rotation.y = this.bread.facing;
    this.bread.drawFace('grin');
    this._renderDeputyDialog();
  }

  _renderDeputyDialog(){
    this._clearPanels();
    const { available, active } = questsForGiver('deputy');
    const questRows = [];
    active.forEach(id => {
      const q = QUESTS[id];
      if (isQuestReady(id)){
        questRows.push(`<div class="q-drow ready"><div><b>${q.title}</b><span>objective complete!</span></div>
          <button class="cafe-btn turnin" data-id="${id}">turn in ✓</button></div>`);
      } else {
        const p = objectiveProgress(id);
        const label = q.objective.kind === 'flag' ? q.objective.noun : `${p.have} / ${p.goal} ${q.objective.noun}`;
        questRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${label}</span></div><span class="q-dpend">in progress</span></div>`);
      }
    });
    available.forEach(id => {
      const q = QUESTS[id];
      const rew = q.reward?.crumbs ? ` · reward 🍞 ${q.reward.crumbs}` : '';
      questRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.desc}${rew}</span></div>
        <button class="cafe-btn accept" data-id="${id}">accept</button></div>`);
    });
    const body = questRows.length ? questRows.join('') : '<p class="q-dnone">"All quiet in the cells. Keep it that way, partner."</p>';

    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Deputy Donut</h2>
        <div class="cafe-sub">sheriff station</div>
        <div class="q-drows">${body}</div>
        <button class="cafe-btn ghost close">done</button>
      </div>`);
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); this.bread.drawFace('yum'); this._renderDeputyDialog(); });
    p.querySelectorAll('.turnin').forEach(b => b.onclick = () => this._onTurnIn(b.dataset.id));
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _onTurnIn(id){
    const reward = turnIn(id);
    if (!reward) return;
    HUD.refreshCrumbs();
    HUD.refreshLumber();
    this.bread.drawFace('yum');
    this._renderDeputyDialog();
  }

  _panel(html, mode = 'panel'){
    const el = document.createElement('div');
    el.className = mode === 'bare' ? 'cafe-panel bare' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.28, 0.62];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    HUD.setSubtitle('sheriff station');
    HUD.say('Sheriff Station. Deputy Donut is at the desk. Sheriff Berry is out on patrol.');
    HUD.setHint('click to walk · talk to Deputy Donut or head back to downtown');
    HUD.hideHintSoon(7000);
  }

  exit(){ this.input.onTap = null; HUD.showEnter(null); HUD.say(''); this._clearPanels(); }

  _tap(ray){
    if (checkNpcClick(ray, this.deputy, this.bread)) return;

    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-6, Math.min(6, gp.x));
    gp.z = Math.max(-5, Math.min(5.5, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  update(dt){
    this.bread.update(dt);
    this.deputy.update(dt, this.bread.root.position);

    let near = null, nd = Infinity;
    this.hotspots.forEach(h => {
      const d = h.pos.distanceTo(this.bread.root.position);
      if (d < nd){ nd = d; near = h; }
    });
    if (near && nd < near.r){
      if (this.nearSpot !== near){
        this.nearSpot = near;
        HUD.showEnter(near.label, () => near.action());
      }
    } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.4, 1.6, this.bread.root.position.z * 0.4), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
